import * as React from 'react'
import styled, { css } from 'styled-components'
import { useInView } from 'react-intersection-observer'

import * as ds from '@bob/design-system'
import * as entity from '@boutique/entities'
import { Framed, Illustration, Video, RichText, ProductCard } from '@bob/rich-text'
import { SocialShare, ShareGroup } from '@bob/components'

type ContentProps = {
  article: entity.article.Article
  setReadTime: (duration: string) => void
  url: string
}

export function Content({ article, setReadTime, url }: ContentProps): React.ReactElement {
  const [shareGroupRef, shareGroupInView] = useInView({
    rootMargin: '85px',
  })
  const [stickyRef, stickyInView] = useInView({
    threshold: 0.05,
    triggerOnce: true,
  })

  const sectionRef = React.useRef<HTMLElement>(null)

  React.useEffect(() => {
    // TODO: count words server side
    if (sectionRef.current?.textContent) {
      const wordCount = sectionRef.current.textContent.split(' ').length
      const readTime = (wordCount / 230).toFixed(0)
      setReadTime(readTime)
    }
  }, [])

  return (
    <>
      <Section ref={sectionRef}>
        <ConditionalDisplay>
          <SocialShare url={url} title={article.title} text={article.description} />
        </ConditionalDisplay>

        <RichTextWrapper>
          <RichText document={article.content.json} links={article.content.links} renderEntry={renderEntry} />
        </RichTextWrapper>
        <Sticky ref={stickyRef} appearTransition={stickyInView} disappearTransition={shareGroupInView}>
          <SocialShare
            column
            buttonLabel={"Partager l'article"}
            url={url}
            title={article.title}
            text={article.description}
          />
        </Sticky>
      </Section>
      <ConditionalDisplay visible ref={shareGroupRef}>
        <ShareGroupStyled url={url} title={article.title} text={article.description} buttonLabel={"Partager l'article"}>
          <Hint>Vous avez aimé cet article ? Partagez-le !</Hint>
        </ShareGroupStyled>
      </ConditionalDisplay>
    </>
  )

  function renderEntry(entry: entity.article.ArticleEntry) {
    if (entry.__typename === 'Illustration') {
      return <Illustration {...entry} />
    }
    if (entry.__typename === 'Video') {
      return <Video {...entry} />
    }
    if (entry.__typename === 'Framed') {
      return <Framed {...entry} />
    }
    if (entry.__typename === 'ProductCard') {
      if (entry.product) {
        return <ProductCard align={entry.align} product={entry.product} />
      }
    }
  }
}

const RichTextWrapper = styled.div`
  width: 100%;
`

const Section = styled.section`
  position: relative;
  overflow: hidden;

  @media screen and (${ds.breakpoint.TABLET}) {
    background-color: ${ds.color.SAND_2};
    border-radius: 12px;
    display: flex;
    padding: 40px 110px;
  }

  ${ds.Button} {
    display: block;
    padding-left: 25%;
    padding-right: 25%;
  }
`

const Sticky = styled.div<{ disappearTransition: boolean; appearTransition: boolean }>`
  ${ds.SocialShare} {
    display: none;

    @media screen and (${ds.breakpoint.TABLET}) {
      display: flex;
      margin-left: 55px;
      opacity: 0;
      position: sticky;
      top: 110px;
      transition-duration: 300ms;
      transition-property: transform, opacity;

      ${({ appearTransition }) => {
        if (appearTransition) {
          return css`
            opacity: 1;
          `
        }
      }}

      ${({ disappearTransition }) => {
        if (disappearTransition) {
          return css`
            transform: translateY(-280px);
            opacity: 0;
          `
        }
      }}
    }
  }
`

const ConditionalDisplay = styled.div<{ visible?: boolean }>`
  height: 120px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  @media screen and (${ds.breakpoint.TABLET}) {
    height: auto;

    ${({ visible }) =>
      visible
        ? css`
            display: flex;
          `
        : css`
            display: none;
          `}
  }
`

const Hint = styled.div`
  margin-bottom: 10px;
`

const ShareGroupStyled = styled(ShareGroup)`
  ${Hint} {
    display: none;

    @media screen and (${ds.breakpoint.TABLET}) {
      display: block;
    }
  }
`
